// src/pages/DietaryPreferences.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import fetchWithAuth from '../utils/fetchWithAuth';
import BackButton from '../components/ui/atoms/BackButton';
import { useToast } from '../context/ToastContext';
import TagToggleButton from '../components/ui/atoms/Button/TagToggleButton';
import IngredientAutocomplete from '../components/Shared/IngredientAutocomplete';
import SectionHeader from '../components/ui/atoms/SectionTitle/SectionHeader';
import { Button } from '../components/ui/atoms/Button/Button';

import { GoMultiSelect } from 'react-icons/go';
import { FaPepperHot, FaDollarSign } from 'react-icons/fa';
import { IoMdBonfire } from 'react-icons/io';
import { MdHeartBroken } from 'react-icons/md';
import { IoMedical } from 'react-icons/io5';
import { FaEarthAmericas } from "react-icons/fa6";
import { useAuth } from '../context/AuthContext';

type Tag = { id: string; name: string; category: string; type: string };
type IngredientEntry = { name: string; catalogId: string | null };

const CATEGORY_CONFIG: Array<{ key: string; label: string; visible: boolean }> =
  [
    { key: 'diet', label: 'Preferred Diet', visible: true },
    { key: 'cuisine', label: 'Cuisine', visible: true },
    { key: 'spice_level', label: 'Spice Level', visible: true },
    { key: 'occasion', label: 'Occasion', visible: false },
    { key: 'technique', label: 'Technique', visible: true },
    { key: 'season', label: 'Season', visible: false },
    { key: 'cost_and_effort', label: 'Cost & Effort', visible: true },
  ];

const SPICE_ORDER: Record<string, number> = {
  none: 0,
  mild: 1,
  medium: 2,
  hot: 3,
};

const formatTagName = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// map category key → icon
const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  diet: GoMultiSelect,
  cuisine: FaEarthAmericas ,
  spice_level: FaPepperHot,
  technique: IoMdBonfire,
  cost_and_effort: FaDollarSign,
};

const DietaryPreferences: React.FC = () => {
  const navigate = useNavigate();
  const { backendUser } = useAuth() as any;
  const location = useLocation();
 
  // if not subscribed _and_ not in onboarding, redirect to subscribe
  useEffect(() => {
    if (
      !backendUser?.isSubscribed &&
      !location.pathname.startsWith('/app/onboarding')
    ) {
      navigate('/app/subscribe', { replace: true });
    }
  }, [backendUser, location.pathname, navigate]);

  const { showToast } = useToast() as any;

  const [tags, setTags] = useState<Tag[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [avoided, setAvoided] = useState<IngredientEntry[]>([]);
  const [allergic, setAllergic] = useState<IngredientEntry[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { manualTags, autoTags } = await fetchWithAuth('/api/tags');
      setTags([
        ...(Object.values(manualTags).flat() as Tag[]),
        ...(Object.values(autoTags).flat() as Tag[]),
      ]);
      const prefs = await fetchWithAuth('/api/user/preferences');
      setExcluded(new Set(prefs.excludedTagIds || []));
      const norm = (arr: any[]): IngredientEntry[] =>
        (arr || []).map((i) =>
          typeof i === 'object'
            ? { name: i.name, catalogId: i.catalogId }
            : { name: String(i), catalogId: null }
        );
      setAvoided(norm(prefs.avoidedIngredients));
      setAllergic(norm(prefs.allergicIngredients));
    })();
  }, []);

  const byCat = useMemo(() => {
    const m: Record<string, Tag[]> = {};
    tags.forEach((t) => {
      const key = t.category.trim().toLowerCase();
      (m[key] ||= []).push(t);
    });
    return m;
  }, [tags]);

  const tagState = (id: string): 'include' | 'exclude' =>
    excluded.has(id) ? 'exclude' : 'include';

  const onTagClick = (id: string) => {
    setDirty(true);
    const updated = new Set(excluded);
    updated.has(id) ? updated.delete(id) : updated.add(id);
    setExcluded(updated);
  };

  const addIng = (
    list: IngredientEntry[],
    setList: React.Dispatch<React.SetStateAction<IngredientEntry[]>>,
    sel: IngredientEntry
  ) => {
    setDirty(true);
    if (!list.some((i) => i.name.toLowerCase() === sel.name.toLowerCase())) {
      setList([...list, sel]);
    }
  };
  const removeIng = (
    list: IngredientEntry[],
    setList: React.Dispatch<React.SetStateAction<IngredientEntry[]>>,
    name: string
  ) => {
    setDirty(true);
    setList(list.filter((i) => i.name !== name));
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await fetchWithAuth('/api/user/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          excludedTagIds: Array.from(excluded),
          avoidedIngredients: avoided.map((i) => i.catalogId ?? i.name),
          allergicIngredients: allergic.map((i) => i.catalogId ?? i.name),
        }),
      });
      showToast({ message: 'Preferences saved!', type: 'success' });
      navigate(-1);
    } catch {
      showToast({ message: 'Save failed', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const sections = CATEGORY_CONFIG.filter((c) => c.visible)
    .map((c) => {
      let list = byCat[c.key] || [];
      if (c.key === 'spice_level') {
        list = [...list].sort(
          (a, b) =>
            (SPICE_ORDER[a.name.toLowerCase()] ?? 99) -
            (SPICE_ORDER[b.name.toLowerCase()] ?? 99)
        );
      }
      return { key: c.key, label: c.label, list };
    })
    .filter((s) => s.list.length > 0);

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-white h-[64px] flex items-center justify-between p-4">
        <BackButton onClick={() => navigate('/app/settings')} />
        <Button
          variant="header"
          onClick={save}
          disabled={!dirty || saving}
          className="ml-auto"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* Content comes directly under the header */}
      <main className="flex-grow px-2 space-y-4 overflow-y-auto ">
        {sections.map(({ key, label, list }) => {
          const Icon = ICON_MAP[key];
          return (
            <section key={key}>
              <SectionHeader>
                {Icon && <Icon className="mr-2 text-white" />}
                {label}
              </SectionHeader>
              <div className="flex flex-wrap gap-2 px-4 py-2">
                {list.map((t) => (
                  <TagToggleButton
                    key={t.id}
                    label={formatTagName(t.name)}
                    state={tagState(t.id)}
                    onClick={() => onTagClick(t.id)}
                  />
                ))}
              </div>
            </section>
          );
        })}

        <section>
          <SectionHeader>
            <MdHeartBroken className="mr-2 text-white" />
            Ingredients You Don’t Like
          </SectionHeader>
          <div className="px-4">
            <IngredientAutocomplete
              placeholder="Add avoided ingredient"
              onSelect={(sel) => addIng(avoided, setAvoided, sel)}
              clearOnSelect
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {avoided.map((i) => (
                <TagToggleButton
                  key={i.name}
                  label={i.name}
                  state="exclude"
                  onClick={() => removeIng(avoided, setAvoided, i.name)}
                />
              ))}
            </div>
          </div>
        </section>

        <section>
          <SectionHeader>
            <IoMedical className="mr-2 text-white" />
            Your Allergies
          </SectionHeader>
          <div className="px-4">
            <IngredientAutocomplete
              placeholder="Add allergic ingredient"
              onSelect={(sel) => addIng(allergic, setAllergic, sel)}
              clearOnSelect
            />
            <div className="flex flex-wrap gap-2 mt-2 pb-20">
              {allergic.map((i) => (
                <TagToggleButton
                  key={i.name}
                  label={i.name}
                  state="exclude"
                  onClick={() => removeIng(allergic, setAllergic, i.name)}
                />
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DietaryPreferences;
